/**
 * GridView Component
 * 
 * Displays files and folders as tiles in a grid layout.
 * Shows file type icons (music note for audio, folder icon for folders, etc.)
 * and displays the file/folder name below each tile.
 * 
 * **Feature: file-explorer-improvements**
 * **Validates: Requirement 10.2** - Grid view displays files as tiles with thumbnails or file type icons
 */

import React from 'react';
import PropTypes from 'prop-types';
import { DownloadIcon, EditIcon, MoveIcon, DeleteIcon } from './Icons';
import { MusicFileIcon, VideoFileIcon, ImageFileIcon, TextFileIcon, PdfFileIcon, ArchiveFileIcon, ProjectFileIcon, GenericFileIcon } from './Icons';
import './GridView.css';

/**
 * Get the appropriate SVG icon component for a file based on its type/extension.
 * Uses the same stroke-based icon style as the action buttons.
 * 
 * @param {string} fileName - The name of the file
 * @param {string} contentType - Optional MIME type of the file
 * @returns {JSX.Element} SVG icon component representing the file type
 */
export const getFileTypeIcon = (fileName, contentType = '') => {
  const size = 40;
  
  // Check content type first
  if (contentType) {
    if (contentType.startsWith('audio/')) return <MusicFileIcon size={size} />;
    if (contentType.startsWith('video/')) return <VideoFileIcon size={size} />;
    if (contentType.startsWith('image/')) return <ImageFileIcon size={size} />;
    if (contentType.startsWith('text/')) return <TextFileIcon size={size} />;
    if (contentType === 'application/pdf') return <PdfFileIcon size={size} />;
    if (contentType.includes('zip') || contentType.includes('compressed')) return <ArchiveFileIcon size={size} />;
  }
  
  // Fall back to extension-based detection
  if (!fileName) return <GenericFileIcon size={size} />;
  
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  // Audio files
  if (['mp3', 'wav', 'flac', 'aac', 'm4a', 'ogg', 'wma', 'aiff', 'aif'].includes(extension)) {
    return <MusicFileIcon size={size} />;
  }
  
  // Video files
  if (['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'].includes(extension)) {
    return <VideoFileIcon size={size} />;
  }
  
  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff'].includes(extension)) {
    return <ImageFileIcon size={size} />;
  }
  
  // PDF
  if (extension === 'pdf') {
    return <PdfFileIcon size={size} />;
  }
  
  // Text/code files
  if (['txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts', 'py', 'java'].includes(extension)) {
    return <TextFileIcon size={size} />;
  }
  
  // Archive files
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    return <ArchiveFileIcon size={size} />;
  }
  
  // Logic Pro / GarageBand / DAW project files
  if (['logicx', 'band', 'ptx', 'als'].includes(extension)) {
    return <ProjectFileIcon size={size} />;
  }
  
  // Default file icon
  return <GenericFileIcon size={size} />;
};

/**
 * Format file size for display.
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size string
 */
const formatFileSize = (bytes) => {
  if (bytes === 0 || bytes === undefined || bytes === null) return '';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Format a date for display
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * GridView Component
 * 
 * Renders files and folders in a grid layout with tiles.
 * Each tile shows an icon representing the file type and the name below.
 * 
 * **Feature: selection-mode-toggle**
 * **Validates: Requirements 1.2, 1.3, 4.2** - Click behavior based on selection mode
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of file objects with key, name, size, contentType
 * @param {Array} props.folders - Array of folder names
 * @param {Set} props.selectedItems - Set of selected item keys
 * @param {boolean} props.isSelectionMode - Whether selection mode is active
 * @param {Function} props.onItemSelect - Callback when an item is selected
 * @param {Function} props.onFolderOpen - Callback when a folder is opened (single-click in Normal Mode)
 * @param {Function} props.onFileDownload - Callback when a file is downloaded (single-click in Normal Mode)
 * @param {Function} props.onFileMove - Callback when a file is moved (Requirement 12.4)
 * @param {Function} props.onItemRename - Callback when an item is renamed
 * @param {Function} props.onItemDelete - Callback when an item is deleted
 * @param {Function} props.onCheckboxClick - Callback when a checkbox is clicked
 * @param {boolean} props.disabled - Whether interactions are disabled
 */
const GridView = ({
  files = [],
  folders = [],
  selectedItems = new Set(),
  isSelectionMode = false,
  onItemSelect,
  onFolderOpen,
  onFileDownload,
  onFileMove,
  onItemRename,
  onItemDelete,
  onCheckboxClick,
  disabled = false,
  // Drag and drop props
  onDragStart,
  onDragEnd,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
  isItemBeingDragged,
  isDropTarget
}) => {
  /**
   * Check if an item is selected.
   * 
   * @param {string} itemKey - The key of the item to check
   * @returns {boolean} True if the item is selected
   */
  const isSelected = (itemKey) => {
    return selectedItems instanceof Set 
      ? selectedItems.has(itemKey) 
      : false;
  };

  /**
   * Handle tile click based on current mode.
   * In Normal Mode: opens folder or downloads file (Requirements 1.2, 1.3)
   * In Selection Mode: toggles selection (Requirement 4.2)
   * 
   * @param {string} itemKey - The key of the clicked item
   * @param {string} itemType - The type of item ('folder' or 'file')
   * @param {Object} event - The click event
   */
  const handleTileClick = (itemKey, itemType, event, itemId = null) => {
    if (disabled) return;
    
    if (isSelectionMode) {
      // Selection Mode: toggle selection (Requirement 4.2)
      if (onItemSelect) {
        onItemSelect(itemKey, event);
      }
    } else {
      // Normal Mode: open folder or download file (Requirements 1.2, 1.3)
      if (itemType === 'folder') {
        if (onFolderOpen) {
          onFolderOpen(itemKey);
        }
      } else {
        if (onFileDownload) {
          // Use item ID for new API, fallback to key for legacy
          onFileDownload(itemId || itemKey);
        }
      }
    }
  };

  /**
   * Handle folder double-click to open.
   * Only works in Normal Mode (single-click already opens in Normal Mode).
   * In Selection Mode, double-click is ignored since single-click toggles selection.
   * 
   * @param {string} folderName - The name of the folder to open
   * @param {Object} event - The double-click event
   */
  const handleFolderDoubleClick = (folderName, event) => {
    if (disabled) return;
    // Double-click only works in Normal Mode
    // In Normal Mode, single-click already opens, so double-click is a no-op
    // This handler is kept for backwards compatibility but effectively disabled
    if (isSelectionMode) return;
    
    event.stopPropagation();
    if (onFolderOpen) {
      onFolderOpen(folderName);
    }
  };

  /**
   * Handle checkbox click for selection toggle.
   * 
   * @param {string} itemKey - The key of the item
   * @param {Object} event - The click event
   */
  const handleCheckboxChange = (itemKey, event) => {
    if (disabled) return;
    event.stopPropagation();
    if (onCheckboxClick) {
      onCheckboxClick(itemKey, event);
    }
  };

  return (
    <div className="grid-view" data-testid="grid-view">
      {/* Folders Section */}
      {folders.length > 0 && (
        <div className="grid-view-section" data-testid="grid-view-folders">
          <h4 className="grid-view-section-title">Folders</h4>
          <div className="grid-view-tiles">
            {folders.map((folder) => {
              const folderKey = folder;
              const selected = isSelected(folderKey);
              const dragging = isItemBeingDragged ? isItemBeingDragged(folderKey) : false;
              const dropTarget = isDropTarget ? isDropTarget(folderKey) : false;
              
              return (
                <div
                  key={folderKey}
                  className={`grid-tile folder-tile ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''} ${dropTarget ? 'drop-target' : ''}`}
                  onClick={(e) => handleTileClick(folderKey, 'folder', e)}
                  onDoubleClick={(e) => handleFolderDoubleClick(folder, e)}
                  draggable={!disabled}
                  onDragStart={(e) => onDragStart && onDragStart(e, folderKey)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => onFolderDragOver && onFolderDragOver(e, folder)}
                  onDragLeave={(e) => onFolderDragLeave && onFolderDragLeave(e)}
                  onDrop={(e) => onFolderDrop && onFolderDrop(e, folder)}
                  data-testid="grid-tile-folder"
                  aria-label={`Folder: ${folder}`}
                >
                  {/* Selection checkbox - only visible in Selection Mode (Requirements 1.4, 4.1) */}
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      className="grid-tile-checkbox"
                      checked={selected}
                      onChange={(e) => handleCheckboxChange(folderKey, e)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={disabled}
                      aria-label={`Select ${folder}`}
                    />
                  )}
                  
                  {/* Folder icon */}
                  <div className="grid-tile-icon folder-icon" data-testid="grid-tile-icon">
                    📁
                  </div>
                  
                  {/* Folder name */}
                  <div className="grid-tile-name" title={folder} data-testid="grid-tile-name">
                    {folder}
                  </div>
                  
                  {/* Hover actions - only visible in Normal Mode (Requirements 2.1, 2.3) */}
                  {!isSelectionMode && (
                    <div className="grid-tile-actions" data-testid={`grid-tile-actions-${folder}`}>
                      {onItemRename && (
                        <button
                          className="grid-tile-action-btn rename-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemRename(folderKey);
                          }}
                          disabled={disabled}
                          title="Rename folder"
                          aria-label={`Rename ${folder}`}
                        >
                          Rename
                        </button>
                      )}
                      {onItemDelete && (
                        <button
                          className="grid-tile-action-btn delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemDelete(folderKey);
                          }}
                          disabled={disabled}
                          title="Delete folder"
                          aria-label={`Delete ${folder}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Files Section */}
      {files.length > 0 && (
        <div className="grid-view-section" data-testid="grid-view-files">
          <h4 className="grid-view-section-title">Files</h4>
          <div className="grid-view-tiles">
            {files.map((file) => {
              const fileKey = file.key;
              const selected = isSelected(fileKey);
              const dragging = isItemBeingDragged ? isItemBeingDragged(fileKey) : false;
              const icon = getFileTypeIcon(file.name, file.contentType);
              
              return (
                <div
                  key={fileKey}
                  className={`grid-tile file-tile ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
                  onClick={(e) => handleTileClick(fileKey, 'file', e, file.id)}
                  draggable={!disabled}
                  onDragStart={(e) => onDragStart && onDragStart(e, fileKey)}
                  onDragEnd={onDragEnd}
                  data-testid="grid-tile-file"
                  aria-label={`File: ${file.name}`}
                >
                  {/* Selection checkbox - only visible in Selection Mode (Requirements 1.4, 4.1) */}
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      className="grid-tile-checkbox"
                      checked={selected}
                      onChange={(e) => handleCheckboxChange(fileKey, e)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={disabled}
                      aria-label={`Select ${file.name}`}
                    />
                  )}
                  
                  {/* File type icon */}
                  <div className="grid-tile-icon file-icon" data-testid="grid-tile-icon">
                    {icon}
                  </div>
                  
                  {/* File name */}
                  <div className="grid-tile-name" title={file.name} data-testid="grid-tile-name">
                    {file.name}
                  </div>
                  {file.status && file.status !== 'COMPLETE' && (
                    <div className="grid-tile-status-badge pending">⚠ Upload incomplete</div>
                  )}
                  
                  {/* File size */}
                  {file.size && (
                    <div className="grid-tile-size" data-testid="grid-tile-size">
                      {formatFileSize(file.size)}
                    </div>
                  )}
                  
                  {/* File date */}
                  {(file.updatedAt || file.createdAt) && (
                    <div className="grid-tile-date" data-testid="grid-tile-date">
                      {formatDate(file.updatedAt || file.createdAt)}
                    </div>
                  )}
                  
                  {/* Hover actions - only visible in Normal Mode (Requirement 2.2) */}
                  {!isSelectionMode && (
                    <div className="grid-tile-actions" data-testid={`grid-tile-actions-${file.name}`}>
                      {onFileDownload && (
                        <button
                          className="grid-tile-action-btn download-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileDownload(file.id || fileKey);
                          }}
                          disabled={disabled || (file.status && file.status !== 'COMPLETE')}
                          title={file.status && file.status !== 'COMPLETE' ? 'Upload incomplete - cannot download' : 'Download'}
                          aria-label={`Download ${file.name}`}
                        >
                          <DownloadIcon size={14} />
                        </button>
                      )}
                      {onItemRename && (
                        <button
                          className="grid-tile-action-btn rename-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemRename(fileKey);
                          }}
                          disabled={disabled}
                          title="Rename"
                          aria-label={`Rename ${file.name}`}
                        >
                          <EditIcon size={14} />
                        </button>
                      )}
                      {onFileMove && (
                        <button
                          className="grid-tile-action-btn move-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileMove(fileKey);
                          }}
                          disabled={disabled}
                          title="Move"
                          aria-label={`Move ${file.name}`}
                        >
                          <MoveIcon size={14} />
                        </button>
                      )}
                      {onItemDelete && (
                        <button
                          className="grid-tile-action-btn delete-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemDelete(fileKey);
                          }}
                          disabled={disabled}
                          title="Delete"
                          aria-label={`Delete ${file.name}`}
                        >
                          <DeleteIcon size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {folders.length === 0 && files.length === 0 && (
        <div className="grid-view-empty" data-testid="grid-view-empty">
          <div className="grid-view-empty-icon">📂</div>
          <p>No files or folders to display</p>
        </div>
      )}
    </div>
  );
};

GridView.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    size: PropTypes.number,
    contentType: PropTypes.string,
    lastModified: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  })),
  folders: PropTypes.arrayOf(PropTypes.string),
  selectedItems: PropTypes.instanceOf(Set),
  isSelectionMode: PropTypes.bool,
  onItemSelect: PropTypes.func,
  onFolderOpen: PropTypes.func,
  onFileDownload: PropTypes.func,
  onFileMove: PropTypes.func,
  onItemRename: PropTypes.func,
  onItemDelete: PropTypes.func,
  onCheckboxClick: PropTypes.func,
  disabled: PropTypes.bool,
  // Drag and drop props
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onFolderDragOver: PropTypes.func,
  onFolderDragLeave: PropTypes.func,
  onFolderDrop: PropTypes.func,
  isItemBeingDragged: PropTypes.func,
  isDropTarget: PropTypes.func
};

export default GridView;
